import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconCode,
  IconDeviceAnalytics,
  IconBrandReact,
  IconCloud,
  IconShieldCheck,
  IconArrowRight,
  IconDeviceMobile,
  IconBolt,
  IconPlayerPlay,
  IconNews,
  IconMedal,
  IconTarget
} from '@tabler/icons-react';

const UBuniverse = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: <IconCode size={40} stroke={1.5} />,
      title: 'Custom Software Development',
      description: 'End-to-end development of scalable web and mobile applications tailored to your unique business requirements.'
    },
    {
      icon: <IconDeviceAnalytics size={40} stroke={1.5} />,
      title: 'SaaS Product Development',
      description: 'From concept to launch, we build subscription-based software products that scale with your business.'
    },
    {
      icon: <IconCloud size={40} stroke={1.5} />,
      title: 'Cloud Architecture',
      description: 'Modern cloud-native solutions using AWS, Firebase, and Google Cloud for maximum reliability and performance.'
    },
    {
      icon: <IconDeviceMobile size={40} stroke={1.5} />,
      title: 'Mobile Applications',
      description: 'Cross-platform mobile apps built with React Native that deliver native performance on iOS and Android.'
    }
  ];

  const athleteFeatures = [
    {
      icon: <IconPlayerPlay size={32} stroke={1.5} />,
      title: 'Pro-Level Video Training',
      description: 'Learn from Olympic medalists and professional athletes through comprehensive video courses.'
    },
    {
      icon: <IconNews size={32} stroke={1.5} />,
      title: 'Curated Sports News',
      description: 'Sport-specific news and insights curated by athletes who understand what matters.'
    },
    {
      icon: <IconMedal size={32} stroke={1.5} />,
      title: 'Expert Instruction',
      description: 'Content created by Olympic medalists, pros, and certified coaches across multiple sports.'
    },
    {
      icon: <IconTarget size={32} stroke={1.5} />,
      title: 'Skill Development',
      description: 'Structured learning paths that take you from fundamentals to advanced techniques.'
    }
  ];

  /* Products data hidden for now - keeping for future use
  const products = [
    {
      title: 'League Management System',
      description: 'A comprehensive platform for organizing tournaments, managing teams, tracking standings, and engaging players across any sport.',
      tags: ['React', 'Firebase', 'Real-time'],
      link: '/leagues'
    }
  ];
  */

  const techStack = [
    { name: 'React', icon: <IconBrandReact size={32} /> },
    { name: 'Node.js', icon: <IconCode size={32} /> },
    { name: 'Firebase', icon: <IconCloud size={32} /> },
    { name: 'TypeScript', icon: <IconShieldCheck size={32} /> }
  ];

  const stats = [
    { value: '1M+', label: 'Athletes Served' },
    { value: '1,700+', label: 'Schools Reached' },
    { value: '100%', label: 'Bespoke Development' },
  ];

  return (
    <main className="ub-saas-home">
      {/* Hero Section */}
      <section className="ub-hero-section">
        <div className="ub-hero-content">
          <div className="ub-hero-badge">
            <IconBolt size={16} />
            <span>Technology & Resources for the Modern Athlete</span>
          </div>
          <h1 className="ub-hero-title">
            We Create Sports Community That <span className="ub-gradient-text">Moves People</span>
          </h1>
          <p className="ub-hero-subtitle">
            UBSports bridges the gap between elite athletes and everyday players. We build
            bespoke sports technology for organizations and deliver world-class training resources
            curated by Olympic and professional athletes to help you master any sport.
          </p>
          <div className="ub-hero-cta">
            <button
              className="ub-cta-primary"
              onClick={() => navigate('/newprojects')}
            >
              <span>Start a Project</span>
              <IconArrowRight size={20} />
            </button>
            <button
              className="ub-cta-secondary"
              onClick={() => navigate('/software')}
            >
              View Our Work
            </button>
          </div>
        </div>
        <div className="ub-hero-visual">
          <div className="ub-hero-image-container">
            <img
              src="/HomePageOne.png"
              alt="Sports Technology Solutions"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.classList.add('ub-hero-placeholder');
              }}
            />
            <div className="ub-hero-image-overlay"></div>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="ub-stats-banner">
        <div className="ub-stats-container">
          {stats.map((stat, index) => (
            <div key={index} className="ub-stat-item">
              <span className="ub-stat-value">{stat.value}</span>
              <span className="ub-stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* B2B: Software Services Section */}
      <section className="ub-services-section">
        <div className="ub-section-container">
          <div className="ub-section-header">
            <span className="ub-section-label">For Organizations</span>
            <h2 className="ub-section-title">Bespoke Software Development</h2>
            <p className="ub-section-subtitle">
              Custom sports technology solutions for leagues, teams, and organizations
            </p>
          </div>
          <div className="ub-services-grid">
            {services.map((service, index) => (
              <div key={index} className="ub-service-card">
                <div className="ub-service-icon">{service.icon}</div>
                <h3 className="ub-service-title">{service.title}</h3>
                <p className="ub-service-description">{service.description}</p>
              </div>
            ))}
          </div>
          <div className="ub-section-cta">
            <button
              className="ub-cta-secondary"
              onClick={() => navigate('/software')}
            >
              View Our Software Work
            </button>
          </div>
        </div>
      </section>

      {/* B2C: UBUnited Section */}
      <section className="ub-ubunited-section">
        <div className="ub-section-container">
          <div className="ub-section-header">
            <span className="ub-section-label ub-label-light">For Athletes</span>
            <h2 className="ub-section-title ub-title-light">UBUnited</h2>
            <p className="ub-section-subtitle ub-subtitle-light">
              Master any sport with video resources, news, and instruction curated
              by Olympic and professional athletes
            </p>
          </div>
          <div className="ub-athlete-features-grid">
            {athleteFeatures.map((feature, index) => (
              <div key={index} className="ub-athlete-feature-card">
                <div className="ub-athlete-feature-icon">{feature.icon}</div>
                <h3 className="ub-athlete-feature-title">{feature.title}</h3>
                <p className="ub-athlete-feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
          <div className="ub-section-cta">
            <button
              className="ub-cta-primary ub-cta-light"
              onClick={() => navigate('/ubunited')}
            >
              <span>Explore UBUnited</span>
              <IconArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Products Section hidden for now - users can access via "View Our Work" button
      <section className="ub-products-section">
        <div className="ub-section-container">
          <div className="ub-section-header">
            <h2 className="ub-section-title">Our Products</h2>
            <p className="ub-section-subtitle">
              Software solutions we've built that are making an impact
            </p>
          </div>
          <div className="ub-products-grid">
            {products.map((product, index) => (
              <div key={index} className="ub-product-card">
                <div className="ub-product-content">
                  <h3 className="ub-product-title">{product.title}</h3>
                  <p className="ub-product-description">{product.description}</p>
                  <div className="ub-product-tags">
                    {product.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="ub-product-tag">{tag}</span>
                    ))}
                  </div>
                </div>
                <div className="ub-product-action">
                  {product.external ? (
                    <a
                      href={product.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ub-product-link"
                    >
                      <span>Explore</span>
                      <IconArrowRight size={18} />
                    </a>
                  ) : (
                    <button
                      className="ub-product-link"
                      onClick={() => navigate(product.link)}
                    >
                      <span>Explore</span>
                      <IconArrowRight size={18} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Technology Section */}
      <section className="ub-tech-section">
        <div className="ub-section-container">
          <div className="ub-section-header">
            <h2 className="ub-section-title">Built with Modern Technology</h2>
            <p className="ub-section-subtitle">
              We use industry-leading tools and frameworks to deliver exceptional results
            </p>
          </div>
          <div className="ub-tech-grid">
            {techStack.map((tech, index) => (
              <div key={index} className="ub-tech-item">
                <div className="ub-tech-icon">{tech.icon}</div>
                <span className="ub-tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ub-cta-section">
        <div className="ub-cta-container">
          <h2 className="ub-cta-title">Ready to Build Something Great?</h2>
          <p className="ub-cta-subtitle">
            Let's discuss your next project and how we can help bring your vision to life.
          </p>
          <div className="ub-cta-buttons">
            <button
              className="ub-cta-primary"
              onClick={() => window.location.href = 'mailto:contact@ubsports.com'}
            >
              <span>Get in Touch</span>
              <IconArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default UBuniverse;
